import { SearchTests } from "./test-list";
import { Link, useQueryState, useLocation, navigate } from "../navigation";
import { useRef, useState } from "react";
import { ArrowRight, Plus, Users } from "lucide-react";
import { ToolDialog } from "../../../../packages/test-ui/tool-dialog";
import { PageHeading, RequestState } from "../PortalShell";
import { AssignForStudent } from "../AssignDialog";
import { useResource, useDebouncedValue, type Student } from "../portal-data";
import { api } from "../api";

type RosterStudent = Student & { stats?: { lastActivityAt: number | null; activeAssignments: number; overdueAssignments: number; completedAttempts: number; latestScorePercent: number | null; needsReview: number } };

const DAY = 24 * 60 * 60;
function activityLabel(at: number | null | undefined) {
  if (!at) return { text: "No activity yet", stale: false };
  const days = Math.floor((Date.now() / 1000 - at) / DAY);
  if (days <= 0) return { text: "Today", stale: false };
  if (days === 1) return { text: "Yesterday", stale: false };
  return { text: `${days} days ago`, stale: days >= 7 };
}

export function AdminStudents({ canManage }: { canManage: boolean }) {
  const location = useLocation();
  const [cursors, setCursors] = useQueryState<string[]>("pages", [""]);
  const [search, setSearch] = useQueryState("search", "");
  const [status, setStatus] = useQueryState("status", "all");
  const [account, setAccount] = useQueryState("account", "all");
  const settledSearch = useDebouncedValue(search);
  const students = useResource<{ students: RosterStudent[]; nextCursor: string | null }>(`/api/admin/students?${new URLSearchParams({ cursor: cursors.at(-1)!, search: settledSearch, status, account, stats: "true" })}`);
  const [adding, setAdding] = useState(() => canManage && location.searchParams.get("new") === "1");
  const [assignTo, setAssignTo] = useState<RosterStudent | null>(null);
  const [confirm, setConfirm] = useState<RosterStudent | null>(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const lock = useRef(false);

  async function change(email: string, active: boolean, label?: string) {
    if (lock.current) return;
    lock.current = true; setBusy(true); setError(""); setMessage("");
    try {
      if (active) {
        await api("/api/admin/access", { method: "POST", body: JSON.stringify({ email, ...(label?.trim() ? { label: label.trim() } : {}) }) });
      } else {
        await api(`/api/admin/access/${encodeURIComponent(email)}`, { method: "DELETE" });
      }
      setMessage(adding ? `${label?.trim() || email} added. They can sign in with that Google account.` : active ? `Access restored for ${email}.` : `Access revoked for ${email}.`);
      setAdding(false); setConfirm(null); setEmail(""); setName(""); students.reload();
      window.dispatchEvent(new Event("sat:portal-invalidated"));
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      lock.current = false; setBusy(false);
    }
  }

  const list = students.data?.students ?? [];
  const readyCount = list.filter((student) => student.signedIn).length;
  const href = (student: RosterStudent) => `/admin/students/${encodeURIComponent(student.id!)}?returnTo=${encodeURIComponent(location.pathname + location.search)}`;
  const filtered = !!search || status !== "all" || account !== "all";

  return (
    <>
      <PageHeading eyebrow="Your roster" title="Students">
        {canManage && (
          <button className="primary-button" onClick={() => { setError(""); setAdding(true); }}>
            <Plus size={15} />Add student
          </button>
        )}
      </PageHeading>
      <div className="portal-content">
        <div className="page-toolbar">
          <SearchTests search={search} onSearch={(value) => { setSearch(value); setCursors([""]); }} placeholder="Search by name or email" />
          <select aria-label="Student access status" value={status} onChange={(event) => { setStatus(event.target.value); setCursors([""]); }}><option value="all">All access</option><option value="active">Allowed</option><option value="revoked">Revoked</option></select>
          <select aria-label="Student account readiness" value={account} onChange={(event) => { setAccount(event.target.value); setCursors([""]); }}><option value="all">All accounts</option><option value="ready">Signed in</option><option value="pending">Awaiting sign-in</option></select>
          <span className="toolbar-note">{list.length} shown · {readyCount} signed in</span>
        </div>
        <RequestState loading={students.loading} error={students.error} retry={students.reload} />
        {message && <p className="success-message" role="status">{message}</p>}
        {error && !adding && !confirm && <p className="error" role="alert">{error}</p>}
        {list.length > 0 && <div className="portal-table-wrap">
          <table className="portal-table roster-table">
            <thead>
              <tr><th>Student</th><th>Last active</th><th>Tests</th><th>Latest</th><th>To review</th><th><span className="sr-only">Actions</span></th></tr>
            </thead>
            <tbody>
              {list.map((student) => {
                const activity = activityLabel(student.stats?.lastActivityAt);
                return <tr key={student.email} className={student.active ? "" : "revoked-row"}>
                  <td data-label="Student">
                    <div className="student-identity">
                      <span className="student-avatar">{student.name.slice(0, 1).toUpperCase()}</span>
                      <div>
                        <strong>{student.id ? <Link href={href(student)}>{student.name}</Link> : student.name}</strong>
                        <small>{student.email}</small>
                        {(!student.active || !student.signedIn) && <span className="roster-flags">{!student.active && <span className="status-badge danger">Revoked</span>}{!student.signedIn && <span className="status-badge neutral">Awaiting sign-in</span>}</span>}
                      </div>
                    </div>
                  </td>
                  <td data-label="Last active"><span className={activity.stale ? "stale" : ""}>{activity.text}</span></td>
                  <td data-label="Tests">{student.stats ? <>{student.stats.activeAssignments} active{student.stats.overdueAssignments > 0 && <small className="late">{student.stats.overdueAssignments} overdue</small>}<small>{student.stats.completedAttempts} completed</small></> : "—"}</td>
                  <td data-label="Latest">{student.stats?.latestScorePercent == null ? "—" : <b>{student.stats.latestScorePercent}%</b>}</td>
                  <td data-label="To review">{student.stats?.needsReview ? <Link className="status-badge accent" href={`/admin/reviews?student=${encodeURIComponent(student.email)}`}>{student.stats.needsReview} to review</Link> : <span className="muted">—</span>}</td>
                  <td data-label="Actions">
                    <div className="assignment-actions">
                      {student.active && student.id && <button className="secondary-button compact" onClick={() => { setMessage(""); setAssignTo(student); }}>Assign</button>}
                      {student.id && <Link className="text-button" href={href(student)}>Open <ArrowRight size={13} /></Link>}
                      {canManage && <button className={`text-button ${student.active ? "danger-text" : ""}`} disabled={busy} onClick={() => { setError(""); if (student.active) setConfirm(student); else void change(student.email, true); }}>{student.active ? "Revoke" : "Restore"}</button>}
                    </div>
                  </td>
                </tr>;
              })}
            </tbody>
          </table>
        </div>}
        {(cursors.length > 1 || students.data?.nextCursor) && <div className="list-pagination"><button className="secondary-button" disabled={cursors.length === 1} onClick={() => setCursors((items) => items.slice(0, -1))}>Previous</button><button className="secondary-button" disabled={!students.data?.nextCursor} onClick={() => setCursors((items) => [...items, students.data!.nextCursor!])}>Next</button></div>}
        {students.data && !list.length && (
          <div className="portal-empty">
            <Users size={24} />
            <h2>{filtered ? "No matching students" : "No students yet"}</h2>
            <p>{filtered ? "Clear the search or filters to see more students." : "Add a student’s Google email to allow access."}</p>
            {filtered ? <button className="secondary-button" onClick={() => { setSearch(""); setStatus("all"); setAccount("all"); setCursors([""]); }}>Clear filters</button> : canManage && <button className="primary-button" onClick={() => setAdding(true)}><Plus size={15} />Add student</button>}
          </div>
        )}
      </div>
      {adding && (
        <ToolDialog title="Add student" className="portal-dialog" onClose={() => { if (!busy) { setAdding(false); if (location.searchParams.get("new")) navigate("/admin/students", true); } }}>
          <form className="portal-form" onSubmit={(event) => { event.preventDefault(); void change(email, true, name); }}>
            <label>
              Name <span className="optional">Optional</span>
              <input value={name} onChange={(event) => setName(event.target.value)} maxLength={160} disabled={busy} placeholder="Student name" autoFocus />
            </label>
            <label>
              Google email
              <input required type="email" value={email} onChange={(event) => setEmail(event.target.value)} disabled={busy} placeholder="student@example.com" />
            </label>
            <p className="field-note">The student signs in with this Google account. You can assign tests right away, before their first sign-in.</p>
            {error && <p className="error" role="alert">{error}</p>}
            <div className="dialog-actions">
              <button type="button" className="secondary-button" disabled={busy} onClick={() => setAdding(false)}>Cancel</button>
              <button className="primary-button" disabled={busy}>{busy ? "Adding…" : "Add student"}</button>
            </div>
          </form>
        </ToolDialog>
      )}
      {confirm && (
        <ToolDialog title={`Revoke access for ${confirm.name}?`} className="portal-dialog" onClose={() => { if (!busy) setConfirm(null); }}>
          <p>{confirm.email} will no longer be able to sign in. Their tests, answers and your notes are kept, and you can restore access at any time.</p>
          {error && <p className="error" role="alert">{error}</p>}
          <div className="dialog-actions">
            <button className="secondary-button" disabled={busy} onClick={() => setConfirm(null)}>Keep access</button>
            <button className="primary-button danger" disabled={busy} onClick={() => void change(confirm.email, false)}>{busy ? "Revoking…" : "Revoke access"}</button>
          </div>
        </ToolDialog>
      )}
      {assignTo && <AssignForStudent student={{ email: assignTo.email, name: assignTo.name }} onClose={() => setAssignTo(null)} onAssigned={() => { setMessage(`Tests assigned to ${assignTo.name}.`); setAssignTo(null); students.reload(); window.dispatchEvent(new Event("sat:portal-invalidated")); }} />}
    </>
  );
}
